import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import * as API from 'api';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { ChannelType, IAction, ICreateIdentity, ICreateUserRequest, IIdentity, ISignUpUser, IUpdateUserRequest, IVerifyIdentity, IVerifyIdentityRequest } from 'types';
import { formatErrorMessages } from 'utils/errors';
import {
  completeSignUpFail,
  completeSignUpSuccess,
  createSignUpUserFail,
  createSignUpUserIdentityFail,
  createSignUpUserIdentitySuccess,
  createSignUpUserSuccess,
  ISignUpMeta,
  resendIdentityVerificationCodeFail,
  resendIdentityVerificationCodeSuccess,
  updateSignUpUserFail,
  updateSignUpUserSuccess,
  verifySignUpUserIdentityFail,
  verifySignUpUserIdentitySuccess,
} from './actions';
import * as ActionTypes from './types';

export function* createSignUpUser({ payload, meta }: IAction<ActionTypes.SignUp.CREATE_SIGN_UP_USER_REQUEST, { body: ICreateUserRequest }, ISignUpMeta>) {
  try {
    const response: ApiResponse<ISignUpUser> = yield call(API.SignUp.createUser, payload.body);

    yield put(createSignUpUserSuccess(response.body));

    if (meta.onSuccess) yield call(meta.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'POST', requestName: 'createSignUpUser' });
    yield put(createSignUpUserFail(formattedError));
  }
}

export function* createSignUpUserIdentity({ payload, meta }: IAction<ActionTypes.SignUp.CREATE_SIGN_UP_USER_IDENTITY_REQUEST, { signUpId: string; identity: ICreateIdentity }, ISignUpMeta>) {
  try {
    const { signUpId, identity } = payload;
    const response: ApiResponse<IIdentity> = yield call(API.SignUp.createIdentity, signUpId, identity);

    yield put(createSignUpUserIdentitySuccess(response.body));

    if (meta.onSuccess) yield call(meta.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'POST', requestName: 'createSignUpUserIdentity' });
    yield put(createSignUpUserIdentityFail(formattedError));
  }
}

export function* verifySignUpUserIdentity({ payload, meta }: IAction<ActionTypes.SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST, { signUpId: string; identityId: string; body: IVerifyIdentityRequest }, ISignUpMeta>) {
  try {
    const { signUpId, identityId, body } = payload;
    const response: ApiResponse<ISignUpUser> = yield call(API.SignUp.verifyIdentity, signUpId, identityId, body);

    yield put(verifySignUpUserIdentitySuccess(response.body));

    if (meta.onSuccess) yield call(meta.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'verifySignUpUserIdentity' });
    yield put(verifySignUpUserIdentityFail(formattedError));
  }
}

export function* resendIdentityVerificationCode({
  payload,
  meta,
}: IAction<
  ActionTypes.SignUp.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST,
  {
    signUpId: string;
    identityId: string;
    channel?: ChannelType.CALL;
  },
  Partial<ISignUpMeta>
>) {
  try {
    const { signUpId, identityId, channel } = payload;
    const response: ApiResponse<IVerifyIdentity> = yield call(API.SignUp.resendVerificationCode, signUpId, identityId, channel);

    yield put(resendIdentityVerificationCodeSuccess(response.body));

    if (meta.successMessage) yield call(toast, SuccessToastContainer({ message: meta.successMessage }));
    if (meta.onSuccess) yield call(meta.onSuccess);
  } catch (error) {
    yield put(resendIdentityVerificationCodeFail(error));
  }
}

export function* updateSignUpUser({ payload, meta }: IAction<ActionTypes.SignUp.UPDATE_SIGN_UP_USER_REQUEST, { signUpId: string; body: IUpdateUserRequest }, ISignUpMeta>) {
  try {
    const { signUpId, body } = payload;
    const response: ApiResponse<ISignUpUser> = yield call(API.SignUp.updateUser, signUpId, body);

    yield put(updateSignUpUserSuccess(response.body));

    if (meta.onSuccess) yield call(meta.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'updateSignUpUser' });
    yield put(updateSignUpUserFail(formattedError));
  }
}

export function* completeSignUp({ payload, meta }: IAction<ActionTypes.SignUp.COMPLETE_SIGN_UP_REQUEST, { signUpId: string }, ISignUpMeta>) {
  try {
    const response: ApiResponse<ISignUpUser> = yield call(API.SignUp.completeSignUp, payload.signUpId);

    yield put(completeSignUpSuccess(response.body));

    if (meta.successMessage) yield call(toast, SuccessToastContainer({ message: meta.successMessage }));
    if (meta.onSuccess) yield call(meta.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'completeSignUp' });
    yield put(completeSignUpFail(formattedError));
  }
}

export function* watchCreateSignUpUser() {
  yield takeEvery(ActionTypes.SignUp.CREATE_SIGN_UP_USER_REQUEST, createSignUpUser);
}

export function* watchCreateSignUpUserIdentity() {
  yield takeEvery(ActionTypes.SignUp.CREATE_SIGN_UP_USER_IDENTITY_REQUEST, createSignUpUserIdentity);
}

export function* watchVerifySignUpUserIdentity() {
  yield takeEvery(ActionTypes.SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST, verifySignUpUserIdentity);
}

export function* watchResendIdentityVerificationCode() {
  yield takeEvery(ActionTypes.SignUp.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST, resendIdentityVerificationCode);
}

export function* watchUpdateSignUpUser() {
  yield takeEvery(ActionTypes.SignUp.UPDATE_SIGN_UP_USER_REQUEST, updateSignUpUser);
}

export function* watchCompleteSignUp() {
  yield takeEvery(ActionTypes.SignUp.COMPLETE_SIGN_UP_REQUEST, completeSignUp);
}

function* signUpSaga() {
  yield all([fork(watchCreateSignUpUser), fork(watchCreateSignUpUserIdentity), fork(watchVerifySignUpUserIdentity), fork(watchResendIdentityVerificationCode), fork(watchUpdateSignUpUser), fork(watchCompleteSignUp)]);
}

export { signUpSaga };
